import {
  AMAZON_JP_SELLER_ID,
  KEEPA_DOMAIN_JP,
  MARKETPLACE_JP,
  SPAPI_ENDPOINT_EU,
  SPAPI_ENDPOINT_FE,
  SPAPI_ENDPOINT_NA,
} from '../constants/marketplaces.js';

/**
 * Pazar profili — bir panelin hangi Amazon pazarına sattığı.
 *
 * **Neden var (13 Ekim 2026, sahip):** amazon.co.uk için JP'nin "birebir aynısı"
 * ayrı bir panel istendi. CA sürümü ayrı bir KOD KOPYASI olarak kurulmuştu ve
 * zamanla ayrıştı (ADR-208: 22 düzeltme bir tarafa yazılıp diğerine taşınmadı).
 * Burada kod tek; panel hangi pazarda çalıştığını `PAZAR` ortam değişkeninden
 * okuyor ve Japonya'ya sabitlenmiş her değer bu profilden geliyor.
 *
 * **Verilmezse JP.** Bugünkü panel hiçbir ayar değişmeden aynen çalışır; JP
 * profilinin her değeri, profilden önce kodda sabit duran değerin aynısı
 * (testler bunu karşılaştırıyor).
 *
 * ⚠️ **Tutarlar en küçük birimde tam sayı.** Veritabanındaki `*_jpy` kolonları
 * ve fiyat motoru tam sayıyla çalışıyor; JP'de bu yen (kuruşsuz), UK'de peni.
 * Gösterirken `ondalik` kadar basamak kaydırılır (bkz. `enKucukBirimdenTutar`).
 */

/**
 * Panel kurulabilen bütün pazarlar. İlk dördü kurulu; 18 Ekim 2026'da 17 pazar daha
 * eklendi (sahip: "UK, MX, SA seçeneklerini eklediğin gibi eksiksiz hatasız şekilde Kanada,
 * Avustralya, … Singapur seçeneklerini de ekle"). Kodda hepsi hazır; sunucuda panel ayrıca
 * kurulur (`docs/YENI-PAZAR-KURULUMU.md`).
 */
export const PAZAR_KODLARI = [
  'JP',
  'UK',
  'MX',
  'SA',
  'CA',
  'AU',
  'AE',
  'EG',
  'BR',
  'TR',
  'FR',
  'DE',
  'ES',
  'IT',
  'IE',
  'BE',
  'SE',
  'PL',
  'NL',
  'IN',
  'SG',
] as const;

export type PazarKodu = (typeof PAZAR_KODLARI)[number];

/**
 * Sunucuda KURULU paneller — hesap menüsündeki "… paneline geç" bağlantıları yalnız bunlar
 * (kurulmamış panelin adresi açılmaz). Yeni panel kurulunca buraya eklenir.
 */
export const KURULU_PAZARLAR: readonly PazarKodu[] = ['JP', 'UK', 'MX', 'SA'];

/** ISO 4217 — panel pazarlarının para birimleri. */
export type ParaBirimi =
  | 'JPY'
  | 'GBP'
  | 'MXN'
  | 'SAR'
  | 'CAD'
  | 'AUD'
  | 'AED'
  | 'EGP'
  | 'BRL'
  | 'TRY'
  | 'EUR'
  | 'SEK'
  | 'PLN'
  | 'INR'
  | 'SGD';

export interface PazarProfili {
  kod: PazarKodu;
  /** Arayüzde görünen ad. */
  ad: string;
  /** Arayüzdeki bayrak. */
  bayrak: string;
  /** Bu pazarın panel adresi — paneller arası geçiş bağlantısı için. */
  panelAdresi: string;
  /** SP-API pazar kimliği. */
  marketplaceId: string;
  /** Alıcı sitesi (ürün bağlantıları). */
  amazonAlanAdi: string;
  /** Satıcı paneli kök adresi (sipariş/ilan bağlantıları). */
  sellerCentral: string;
  /** SP-API bölgesel uç noktası. */
  spapiUcNoktasi: string;
  /** SP-API bildirimlerinin (SQS) bulunduğu AWS bölgesi. */
  awsBolgesi: string;
  /** ISO 4217 para birimi. */
  paraBirimi: ParaBirimi;
  paraIsareti: string;
  /** En küçük birimin ondalık basamağı: yen 0; diğerlerinin hepsi 2 (peni, cent, kuruş…). */
  ondalik: 0 | 2;
  /** Sayı/para biçimlendirme yereli. */
  yerel: string;
  /** SP-API ilan yüklerindeki `language_tag`. */
  ilanDili: string;
  /**
   * SP-API katalog isteğinin `locale` değeri: pazarın varsayılan dili İngilizce değilse kategori ve
   * başlık İngilizce gelsin diye (3 Kas 2026, sahip: "kategoriler her ülkede İngilizce yazsın").
   * Boşsa pazarın varsayılanı; UK ve SA zaten İngilizce döndürüyor (üretimde ölçüldü).
   */
  katalogDili?: string;
  /** Gün sınırları ve zamanlanmış işler bu saate göre. */
  saatDilimi: string;
  /**
   * Keepa'nın bu pazar için alan numarası. `null`: Keepa bu pazarı desteklemiyor
   * (amazon.sa) — sipariş detayındaki Keepa bağlantısı gösterilmez. Kaynak (ABD)
   * verisi her pazarda Keepa'nın amazon.com alanından geliyor; bu alan yalnız
   * hedef pazarın Keepa sayfası bağlantısı için.
   */
  keepaDomain: number | null;
  /** Amazon'un bu pazardaki kendi satıcı kimliği (rakip süzgeçleri). */
  amazonSaticiKimligi: string;
  /** Varsayılan satış vergisi oranı (0.10 = %10) — ayarlardan değiştirilebilir. */
  satisVergisiOrani: number;
  /** Arayüzde vergiye verilen ad (kısa): JP "JCT", UK "VAT". */
  vergiAdi: string;
  /** Cümle içinde: JP "tüketim vergisi", UK "KDV". */
  vergiKisa: string;
  /** JP "tüketim vergisi (JCT)", UK "KDV (VAT)". */
  vergiTuru: string;
  /** JP "Japon tüketim vergisi (JCT)", UK "İngiltere KDV'si (VAT)". */
  vergiUzun: string;
  /** Belirtme hâli: JP "Japon tüketim vergisini (JCT)", UK "İngiltere KDV'sini (VAT)". */
  vergiUzunBelirtme: string;
}

/** amazon.co.uk pazar kimliği. */
export const MARKETPLACE_UK = 'A1F83G8C2ARO7P' as const;
/** Keepa: 2 = amazon.co.uk. */
export const KEEPA_DOMAIN_UK = 2 as const;
/** Amazon.co.uk'nin kendi satıcı kimliği. */
export const AMAZON_UK_SELLER_ID = 'A3P5ROKL5A1OLE' as const;

/** amazon.com.mx pazar kimliği (14 Ekim 2026, MX paneli). */
export const MARKETPLACE_MX = 'A1AM78C64UM0Y8' as const;
/** Keepa: 11 = amazon.com.mx. */
export const KEEPA_DOMAIN_MX = 11 as const;
/**
 * Amazon.com.mx'in kendi (perakende) satıcı kimliği — rakip süzgeçlerinde
 * "Amazon'un kendi teklifi"ni ayırmak için. Repricer araçlarının yaygın
 * listesinden; sahibin ilk MX tekliflerinde doğrulanmalı (docs/MX-PAZARI.md).
 */
export const AMAZON_MX_SELLER_ID = 'AVDBXBAVVSXLQ' as const;

/** amazon.sa pazar kimliği (15 Ekim 2026, SA paneli). Avrupa bölgesi (UK ile aynı uç nokta). */
export const MARKETPLACE_SA = 'A17E79C6D8DWNP' as const;

/**
 * KDV'li (fiyata dahil) pazarların vergi metinleri: "Fransa KDV'si (TVA)". Türkçede "KDV'si"
 * eki ülke adından bağımsız.
 */
function kdvMetinleri(ulke: string, vergiAdi: string) {
  return {
    vergiAdi,
    vergiKisa: 'KDV',
    vergiTuru: `KDV (${vergiAdi})`,
    vergiUzun: `${ulke} KDV'si (${vergiAdi})`,
    vergiUzunBelirtme: `${ulke} KDV'sini (${vergiAdi})`,
  };
}

const AVRUPA = { spapiUcNoktasi: SPAPI_ENDPOINT_EU, awsBolgesi: 'eu-west-1' } as const;
const KUZEY_AMERIKA = { spapiUcNoktasi: SPAPI_ENDPOINT_NA, awsBolgesi: 'us-east-1' } as const;
const UZAK_DOGU = { spapiUcNoktasi: SPAPI_ENDPOINT_FE, awsBolgesi: 'us-west-2' } as const;
/** Avrupa pazarlarının ortak satıcı paneli (Amazon AB'de tek Seller Central kullanıyor). */
const SELLER_CENTRAL_AVRUPA = 'https://sellercentral-europe.amazon.com';

export const PAZAR_PROFILLERI: Readonly<Record<PazarKodu, PazarProfili>> = {
  JP: {
    kod: 'JP',
    ad: 'Japonya',
    bayrak: '🇯🇵',
    panelAdresi: 'https://amzjp.kerimsametkilic.com',
    marketplaceId: MARKETPLACE_JP,
    amazonAlanAdi: 'amazon.co.jp',
    sellerCentral: 'https://sellercentral.amazon.co.jp',
    spapiUcNoktasi: SPAPI_ENDPOINT_FE,
    awsBolgesi: 'us-west-2',
    paraBirimi: 'JPY',
    paraIsareti: '¥',
    ondalik: 0,
    yerel: 'ja-JP',
    ilanDili: 'ja_JP',
    katalogDili: 'en_US',
    saatDilimi: 'Asia/Tokyo',
    keepaDomain: KEEPA_DOMAIN_JP,
    amazonSaticiKimligi: AMAZON_JP_SELLER_ID,
    satisVergisiOrani: 0.1,
    vergiAdi: 'JCT',
    vergiKisa: 'tüketim vergisi',
    vergiTuru: 'tüketim vergisi (JCT)',
    vergiUzun: 'Japon tüketim vergisi (JCT)',
    vergiUzunBelirtme: 'Japon tüketim vergisini (JCT)',
  },
  UK: {
    kod: 'UK',
    ad: 'Birleşik Krallık',
    bayrak: '🇬🇧',
    panelAdresi: 'https://amzuk.kerimsametkilic.com',
    marketplaceId: MARKETPLACE_UK,
    amazonAlanAdi: 'amazon.co.uk',
    sellerCentral: 'https://sellercentral.amazon.co.uk',
    spapiUcNoktasi: SPAPI_ENDPOINT_EU,
    awsBolgesi: 'eu-west-1',
    paraBirimi: 'GBP',
    paraIsareti: '£',
    ondalik: 2,
    yerel: 'en-GB',
    ilanDili: 'en_GB',
    saatDilimi: 'Europe/London',
    keepaDomain: KEEPA_DOMAIN_UK,
    amazonSaticiKimligi: AMAZON_UK_SELLER_ID,
    satisVergisiOrani: 0.2,
    vergiAdi: 'VAT',
    vergiKisa: 'KDV',
    vergiTuru: 'KDV (VAT)',
    vergiUzun: "İngiltere KDV'si (VAT)",
    vergiUzunBelirtme: "İngiltere KDV'sini (VAT)",
  },
  /*
   * MX (14 Ekim 2026, sahip: "birebir aynı versiyonunu meksika için de").
   * Amazon.com.mx Kuzey Amerika bölgesinde: SP-API uç noktası ABD'ninkiyle aynı
   * (`-na`), SQS us-east-1. Para MXN, en küçük birim centavo (2 basamak) —
   * UK'deki peni gibi. Gösterimde "MX$": panel ABD maliyetlerini "$" ile
   * gösteriyor, karışmasın (fatura modülü de MXN'i "MX$" yazıyor).
   * Vergi: IVA %16 — muhasebeciyle teyit edilecek (docs/MX-PAZARI.md).
   */
  MX: {
    kod: 'MX',
    ad: 'Meksika',
    bayrak: '🇲🇽',
    panelAdresi: 'https://amzmx.kerimsametkilic.com',
    marketplaceId: MARKETPLACE_MX,
    amazonAlanAdi: 'amazon.com.mx',
    sellerCentral: 'https://sellercentral.amazon.com.mx',
    spapiUcNoktasi: SPAPI_ENDPOINT_NA,
    awsBolgesi: 'us-east-1',
    paraBirimi: 'MXN',
    paraIsareti: 'MX$',
    ondalik: 2,
    yerel: 'es-MX',
    ilanDili: 'es_MX',
    katalogDili: 'en_US',
    saatDilimi: 'America/Mexico_City',
    keepaDomain: KEEPA_DOMAIN_MX,
    amazonSaticiKimligi: AMAZON_MX_SELLER_ID,
    satisVergisiOrani: 0.16,
    vergiAdi: 'IVA',
    vergiKisa: 'KDV',
    vergiTuru: 'KDV (IVA)',
    vergiUzun: "Meksika KDV'si (IVA)",
    vergiUzunBelirtme: "Meksika KDV'sini (IVA)",
  },
  /**
   * Suudi Arabistan (15 Ekim 2026, SA paneli). amazon.sa Avrupa SP-API bölgesinde
   * (sellingpartnerapi-eu, SQS eu-west-1) — UK ile aynı uç nokta, ayrı pazar kimliği.
   * Para SAR, en küçük birim halala (1/100). Yerel `en-SA`: `ar-SA` Arapça rakam
   * üretiyor ("١٢٫٩٩"). Vergi: KDV (VAT) %15 — muhasebeciyle teyit (docs/SA-PAZARI.md).
   *
   * ⚠️ Keepa amazon.sa'yı desteklemiyor → `keepaDomain: null`.
   * ⚠️ Amazon.sa'nın kendi (perakende) satıcı kimliği hiçbir kaynakta yok; bilinmeden
   * boş bırakıldı → "Amazon'un teklifini hariç tut" bu pazarda Amazon'u tanımaz.
   * İlk gerçek tekliflerde "Amazon.sa" satıcısının kimliği görülüp buraya yazılmalı.
   * İlan dili (`language_tag` gönderilmiyor, bilgi amaçlı): amazon.sa en_AE / ar_AE.
   */
  SA: {
    kod: 'SA',
    ad: 'Suudi Arabistan',
    bayrak: '🇸🇦',
    panelAdresi: 'https://amzsa.kerimsametkilic.com',
    marketplaceId: MARKETPLACE_SA,
    amazonAlanAdi: 'amazon.sa',
    sellerCentral: 'https://sellercentral.amazon.sa',
    spapiUcNoktasi: SPAPI_ENDPOINT_EU,
    awsBolgesi: 'eu-west-1',
    paraBirimi: 'SAR',
    paraIsareti: 'SAR',
    ondalik: 2,
    yerel: 'en-SA',
    ilanDili: 'en_AE',
    saatDilimi: 'Asia/Riyadh',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.15,
    vergiAdi: 'VAT',
    vergiKisa: 'KDV',
    vergiTuru: 'KDV (VAT)',
    vergiUzun: "Suudi Arabistan KDV'si (VAT)",
    vergiUzunBelirtme: "Suudi Arabistan KDV'sini (VAT)",
  },
  /*
   * ─── 18 Ekim 2026: 17 yeni pazar ─────────────────────────────────────────────
   *
   * Pazar kimlikleri, uç noktalar ve AWS bölgeleri python-amazon-sp-api'nin
   * `Marketplaces` tablosuyla karşılaştırıldı (birebir aynı). Para birimlerinin hepsi
   * 2 basamaklı. Yereller Latin rakam üretenlerden seçildi (`ar-*` Arapça rakam veriyor).
   *
   * Keepa yalnız com, co.uk, de, fr, co.jp, ca, it, es, in, com.mx, com.br alanlarını
   * destekliyor; diğerlerinde `keepaDomain: null` (kaynak ABD verisi yine amazon.com'dan).
   *
   * Amazon'un kendi (perakende) satıcı kimliği yalnız güvenilir bilinen pazarlarda dolu
   * (CA, DE, FR, IT, ES; AU yaygın listeden, doğrulanmalı). Bilinmeyenler boş: o pazarda
   * "Amazon'un teklifini hariç tut" Amazon'u tanımaz — ilk gerçek tekliflerde görülen
   * kimlik buraya yazılmalı (SA ile aynı).
   *
   * ⚠️ Vergi oranları VARSAYILAN; kurulumda muhasebeciyle teyit edilmeli. Fiyata vergi
   * dahil olmayan pazarlarda (Kanada: GST/HST ödeme sırasında eklenir) oran 0. Brezilya
   * ve Hindistan'da vergi satıcının yerel şirketine bağlı.
   */
  CA: {
    kod: 'CA',
    ad: 'Kanada',
    bayrak: '🇨🇦',
    // ⚠️ amzca adresinde eski, ayrı kodlu CA paneli çalışıyor (/opt/amzca) — bkz. docs/YENI-PAZAR-KURULUMU.md
    panelAdresi: 'https://amzca.kerimsametkilic.com',
    marketplaceId: 'A2EUQ1WTGCTBG2',
    amazonAlanAdi: 'amazon.ca',
    sellerCentral: 'https://sellercentral.amazon.ca',
    ...KUZEY_AMERIKA,
    paraBirimi: 'CAD',
    paraIsareti: 'C$',
    ondalik: 2,
    yerel: 'en-CA',
    ilanDili: 'en_CA',
    saatDilimi: 'America/Toronto',
    keepaDomain: 6,
    amazonSaticiKimligi: 'A3DWYIK6Y9EEQB',
    // GST/HST fiyata dahil değil — Amazon ödeme sırasında ekleyip tahsil ediyor
    satisVergisiOrani: 0,
    vergiAdi: 'GST/HST',
    vergiKisa: 'satış vergisi',
    vergiTuru: 'satış vergisi (GST/HST)',
    vergiUzun: 'Kanada satış vergisi (GST/HST)',
    vergiUzunBelirtme: 'Kanada satış vergisini (GST/HST)',
  },
  AU: {
    kod: 'AU',
    ad: 'Avustralya',
    bayrak: '🇦🇺',
    panelAdresi: 'https://amzau.kerimsametkilic.com',
    marketplaceId: 'A39IBJ37TRP1C6',
    amazonAlanAdi: 'amazon.com.au',
    sellerCentral: 'https://sellercentral.amazon.com.au',
    ...UZAK_DOGU,
    paraBirimi: 'AUD',
    paraIsareti: 'A$',
    ondalik: 2,
    yerel: 'en-AU',
    ilanDili: 'en_AU',
    saatDilimi: 'Australia/Sydney',
    keepaDomain: null,
    // Yaygın listeden — ilk tekliflerde doğrulanmalı (MX gibi)
    amazonSaticiKimligi: 'ANEGB3WVEVKZB',
    satisVergisiOrani: 0.1,
    ...kdvMetinleri('Avustralya', 'GST'),
  },
  AE: {
    kod: 'AE',
    ad: 'Birleşik Arap Emirlikleri',
    bayrak: '🇦🇪',
    panelAdresi: 'https://amzae.kerimsametkilic.com',
    marketplaceId: 'A2VIGQ35RCS4UG',
    amazonAlanAdi: 'amazon.ae',
    sellerCentral: 'https://sellercentral.amazon.ae',
    ...AVRUPA,
    paraBirimi: 'AED',
    paraIsareti: 'AED',
    ondalik: 2,
    yerel: 'en-AE',
    ilanDili: 'en_AE',
    saatDilimi: 'Asia/Dubai',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.05,
    ...kdvMetinleri('Birleşik Arap Emirlikleri', 'VAT'),
  },
  EG: {
    kod: 'EG',
    ad: 'Mısır',
    bayrak: '🇪🇬',
    panelAdresi: 'https://amzeg.kerimsametkilic.com',
    marketplaceId: 'ARBP9OOSHTCHU',
    amazonAlanAdi: 'amazon.eg',
    sellerCentral: 'https://sellercentral.amazon.eg',
    ...AVRUPA,
    paraBirimi: 'EGP',
    paraIsareti: 'EGP',
    ondalik: 2,
    yerel: 'en-EG',
    ilanDili: 'en_AE',
    saatDilimi: 'Africa/Cairo',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.14,
    ...kdvMetinleri('Mısır', 'VAT'),
  },
  BR: {
    kod: 'BR',
    ad: 'Brezilya',
    bayrak: '🇧🇷',
    panelAdresi: 'https://amzbr.kerimsametkilic.com',
    marketplaceId: 'A2Q3Y263D00KWC',
    amazonAlanAdi: 'amazon.com.br',
    sellerCentral: 'https://sellercentral.amazon.com.br',
    ...KUZEY_AMERIKA,
    paraBirimi: 'BRL',
    paraIsareti: 'R$',
    ondalik: 2,
    yerel: 'pt-BR',
    ilanDili: 'pt_BR',
    saatDilimi: 'America/Sao_Paulo',
    keepaDomain: 12,
    amazonSaticiKimligi: '',
    // Vergi (ICMS/PIS/COFINS) satıcının Brezilya şirketine (CNPJ) ve eyalete bağlı — muhasebeciyle girilir
    satisVergisiOrani: 0,
    vergiAdi: 'ICMS',
    vergiKisa: 'satış vergisi',
    vergiTuru: 'satış vergisi (ICMS)',
    vergiUzun: 'Brezilya satış vergisi (ICMS)',
    vergiUzunBelirtme: 'Brezilya satış vergisini (ICMS)',
  },
  TR: {
    kod: 'TR',
    ad: 'Türkiye',
    bayrak: '🇹🇷',
    panelAdresi: 'https://amztr.kerimsametkilic.com',
    marketplaceId: 'A33AVAJ2PDY3EV',
    amazonAlanAdi: 'amazon.com.tr',
    sellerCentral: 'https://sellercentral.amazon.com.tr',
    ...AVRUPA,
    paraBirimi: 'TRY',
    paraIsareti: '₺',
    ondalik: 2,
    yerel: 'tr-TR',
    ilanDili: 'tr_TR',
    saatDilimi: 'Europe/Istanbul',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.2,
    vergiAdi: 'KDV',
    vergiKisa: 'KDV',
    vergiTuru: 'KDV',
    vergiUzun: "Türkiye KDV'si",
    vergiUzunBelirtme: "Türkiye KDV'sini",
  },
  FR: {
    kod: 'FR',
    ad: 'Fransa',
    bayrak: '🇫🇷',
    panelAdresi: 'https://amzfr.kerimsametkilic.com',
    marketplaceId: 'A13V1IB3VIYZZH',
    amazonAlanAdi: 'amazon.fr',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'fr-FR',
    ilanDili: 'fr_FR',
    saatDilimi: 'Europe/Paris',
    keepaDomain: 4,
    amazonSaticiKimligi: 'A1X6FK5RDHNB96',
    satisVergisiOrani: 0.2,
    ...kdvMetinleri('Fransa', 'TVA'),
  },
  DE: {
    kod: 'DE',
    ad: 'Almanya',
    bayrak: '🇩🇪',
    panelAdresi: 'https://amzde.kerimsametkilic.com',
    marketplaceId: 'A1PA6795UKMFR9',
    amazonAlanAdi: 'amazon.de',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'de-DE',
    ilanDili: 'de_DE',
    saatDilimi: 'Europe/Berlin',
    keepaDomain: 3,
    amazonSaticiKimligi: 'A3JWKAKR8XB7XF',
    satisVergisiOrani: 0.19,
    ...kdvMetinleri('Almanya', 'MwSt'),
  },
  ES: {
    kod: 'ES',
    ad: 'İspanya',
    bayrak: '🇪🇸',
    panelAdresi: 'https://amzes.kerimsametkilic.com',
    marketplaceId: 'A1RKKUPIHCS9HS',
    amazonAlanAdi: 'amazon.es',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'es-ES',
    ilanDili: 'es_ES',
    saatDilimi: 'Europe/Madrid',
    keepaDomain: 9,
    amazonSaticiKimligi: 'A1AT7YVPFBWXBL',
    satisVergisiOrani: 0.21,
    ...kdvMetinleri('İspanya', 'IVA'),
  },
  IT: {
    kod: 'IT',
    ad: 'İtalya',
    bayrak: '🇮🇹',
    panelAdresi: 'https://amzit.kerimsametkilic.com',
    marketplaceId: 'APJ6JRA9NG5V4',
    amazonAlanAdi: 'amazon.it',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'it-IT',
    ilanDili: 'it_IT',
    saatDilimi: 'Europe/Rome',
    keepaDomain: 8,
    amazonSaticiKimligi: 'A11IL2PNWYJU7H',
    satisVergisiOrani: 0.22,
    ...kdvMetinleri('İtalya', 'IVA'),
  },
  IE: {
    kod: 'IE',
    ad: 'İrlanda',
    bayrak: '🇮🇪',
    panelAdresi: 'https://amzie.kerimsametkilic.com',
    marketplaceId: 'A28R8C7NBKEWEA',
    amazonAlanAdi: 'amazon.ie',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'en-IE',
    ilanDili: 'en_IE',
    saatDilimi: 'Europe/Dublin',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.23,
    ...kdvMetinleri('İrlanda', 'VAT'),
  },
  BE: {
    kod: 'BE',
    ad: 'Belçika',
    bayrak: '🇧🇪',
    panelAdresi: 'https://amzbe.kerimsametkilic.com',
    marketplaceId: 'AMEN7PMS3EDWL',
    amazonAlanAdi: 'amazon.com.be',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'fr-BE',
    ilanDili: 'fr_BE',
    saatDilimi: 'Europe/Brussels',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.21,
    ...kdvMetinleri('Belçika', 'TVA/BTW'),
  },
  SE: {
    kod: 'SE',
    ad: 'İsveç',
    bayrak: '🇸🇪',
    panelAdresi: 'https://amzse.kerimsametkilic.com',
    marketplaceId: 'A2NODRKZP88ZB9',
    amazonAlanAdi: 'amazon.se',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'SEK',
    // "kr" Norveç/Danimarka ile karışır — ISO kodu
    paraIsareti: 'SEK',
    ondalik: 2,
    yerel: 'sv-SE',
    ilanDili: 'sv_SE',
    saatDilimi: 'Europe/Stockholm',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.25,
    ...kdvMetinleri('İsveç', 'moms'),
  },
  PL: {
    kod: 'PL',
    ad: 'Polonya',
    bayrak: '🇵🇱',
    panelAdresi: 'https://amzpl.kerimsametkilic.com',
    marketplaceId: 'A1C3SOZRARQ6R3',
    amazonAlanAdi: 'amazon.pl',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'PLN',
    paraIsareti: 'zł',
    ondalik: 2,
    yerel: 'pl-PL',
    ilanDili: 'pl_PL',
    saatDilimi: 'Europe/Warsaw',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.23,
    ...kdvMetinleri('Polonya', 'VAT'),
  },
  NL: {
    kod: 'NL',
    ad: 'Hollanda',
    bayrak: '🇳🇱',
    panelAdresi: 'https://amznl.kerimsametkilic.com',
    marketplaceId: 'A1805IZSGTT6HS',
    amazonAlanAdi: 'amazon.nl',
    sellerCentral: SELLER_CENTRAL_AVRUPA,
    ...AVRUPA,
    paraBirimi: 'EUR',
    paraIsareti: '€',
    ondalik: 2,
    yerel: 'nl-NL',
    ilanDili: 'nl_NL',
    saatDilimi: 'Europe/Amsterdam',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.21,
    ...kdvMetinleri('Hollanda', 'BTW'),
  },
  IN: {
    kod: 'IN',
    ad: 'Hindistan',
    bayrak: '🇮🇳',
    panelAdresi: 'https://amzin.kerimsametkilic.com',
    marketplaceId: 'A21TJRUUN4KGV',
    amazonAlanAdi: 'amazon.in',
    sellerCentral: 'https://sellercentral.amazon.in',
    ...AVRUPA,
    paraBirimi: 'INR',
    paraIsareti: '₹',
    ondalik: 2,
    yerel: 'en-IN',
    ilanDili: 'en_IN',
    saatDilimi: 'Asia/Kolkata',
    keepaDomain: 10,
    amazonSaticiKimligi: '',
    // GST ürün grubuna göre %5–28; yaygın oran %18 — satıcının GSTIN'ine göre muhasebeciyle girilir
    satisVergisiOrani: 0.18,
    ...kdvMetinleri('Hindistan', 'GST'),
  },
  SG: {
    kod: 'SG',
    ad: 'Singapur',
    bayrak: '🇸🇬',
    panelAdresi: 'https://amzsg.kerimsametkilic.com',
    marketplaceId: 'A19VAU5U5O7RUS',
    amazonAlanAdi: 'amazon.sg',
    sellerCentral: 'https://sellercentral.amazon.sg',
    ...UZAK_DOGU,
    paraBirimi: 'SGD',
    paraIsareti: 'S$',
    ondalik: 2,
    yerel: 'en-SG',
    ilanDili: 'en_SG',
    saatDilimi: 'Asia/Singapore',
    keepaDomain: null,
    amazonSaticiKimligi: '',
    satisVergisiOrani: 0.09,
    ...kdvMetinleri('Singapur', 'GST'),
  },
};

/**
 * Ortam değerinden profil. Boş/tanımsız → JP (bugünkü panel). Tanınmayan değer
 * HATA — yanlış yazılmış bir ayar sessizce JP'ye düşüp UK panelini Japonya
 * pazarına bağlamamalı.
 */
export function pazarProfili(kod: string | null | undefined): PazarProfili {
  const temiz = (kod ?? '').trim().toUpperCase();
  if (temiz === '') return PAZAR_PROFILLERI.JP;
  if (pazarKoduMu(temiz)) return PAZAR_PROFILLERI[temiz];
  throw new Error(
    `Bilinmeyen PAZAR değeri: "${kod}". Geçerli değerler: ${PAZAR_KODLARI.join(', ')}.`,
  );
}

export function pazarKoduMu(kod: string): kod is PazarKodu {
  return (PAZAR_KODLARI as readonly string[]).includes(kod);
}

/** En küçük birim (yen / peni) → para birimi tutarı: UK 1299 → 12,99. */
export function enKucukBirimdenTutar(deger: number, profil: PazarProfili): number {
  return profil.ondalik === 0 ? deger : deger / 10 ** profil.ondalik;
}

/** Para gösterimi: JP `¥12,345` · UK `£12.99` · MX `$12.99` (Intl, es-MX) · SA `SAR 12.99` (en-SA). */
export function paraBicimle(enKucukBirim: number, profil: PazarProfili): string {
  return new Intl.NumberFormat(profil.yerel, {
    style: 'currency',
    currency: profil.paraBirimi,
    minimumFractionDigits: profil.ondalik,
    maximumFractionDigits: profil.ondalik,
  }).format(enKucukBirimdenTutar(enKucukBirim, profil));
}

/** Ürün sayfası bağlantısı. */
export function urunBaglantisi(asin: string, profil: PazarProfili): string {
  return `https://www.${profil.amazonAlanAdi}/dp/${asin}`;
}

/** Pazarın ISO 3166-1 ülke kodu. Pazar kodlarımız ISO ile aynı; tek istisna UK (ISO'da GB). */
export function pazarUlkeKodu(profil: PazarProfili): string {
  return profil.kod === 'UK' ? 'GB' : profil.kod;
}

/** Elle yazılan ülke kodu: "UK" yaygın yazım ama ISO'da GB. */
export function ulkeKoduNormalize(kod: string): string {
  const temiz = kod.trim().toUpperCase();
  return temiz === 'UK' ? 'GB' : temiz;
}

/** ISO ülke kodunun Türkçe adı; pazarı olmayan ülkede kodun kendisi. */
export function ulkeAdi(ulkeKodu: string): string {
  const kod = ulkeKoduNormalize(ulkeKodu);
  return Object.values(PAZAR_PROFILLERI).find((p) => pazarUlkeKodu(p) === kod)?.ad ?? kod;
}

/**
 * Depo alıcı adresi panelin ülkesinde değilse kullanıcıya söylenecek hata; uyuyorsa `null`.
 *
 * ⚠️ Panel kurulumu bir dönem JP'nin alıcı adresini de kopyalıyordu: UK/MX/SA panellerinde kargo
 * JAPONYA için soruluyor, fiyat kilitli yazılıyor ve satış fiyatı ondan hesaplanıyordu. JP paneli
 * hariç: salt okunur, bu kural ona bir şey kazandırmıyor.
 */
export function aliciUlkesiHatasi(profil: PazarProfili, ulkeKodu: string): string | null {
  if (profil.kod === 'JP') return null;
  if (ulkeKoduNormalize(ulkeKodu) === pazarUlkeKodu(profil)) return null;
  return `Depo alıcı adresi ${ulkeAdi(ulkeKodu)} ülkesinde, panel ${profil.ad}; Ayarlar'dan adresi düzeltin.`;
}
